import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ddm, ddmSv, h } from '../format.js';
import { DICT, useLang, useT, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Icon } from '../shell.js';
import { openTimer } from '../timer.js';
import { AvatarStack, Callout, PageHead, toast } from '../ui.js';
import type { Me } from '../App.js';
import { barPct, look, pctText, type TeamView } from './Team.js';
import { NewsOnNow } from './News.js';
import { AbsenceBlock } from './Absence.js';
import { StageBar, type NextStep, type StageView } from './Projects.js';
import { WeekCalendar } from './WeekCalendar.js';
import { MentionsOnNow } from './Notes.js';
import { GettingStarted, guideWork } from '../guide.js';
import { ReviewsWaiting } from './Review.js';
import { HiddenProjects, HideProject, type HiddenProject } from './hideProject.js';
import { SendForReview } from './sendForReview.js';
import { openZiflow } from './ziflowLink.js';
import { RushesBanners } from './rushes.js';
import { MyTasks } from './stageTasks.js';
import { PrepBanners } from './shootPrep.js';
import { FoldHead, useFold } from '../fold.js';
import { greeting, isCrewView, TodayFailed, TodayNews, TodayShoots, todaySummary, useToday } from './Today.js';

export interface MyWorkRow {
  projectId: string;
  projectName: string;
  client: string;
  videoId: string;
  videoName: string;
  stage: StageView;
  next: NextStep;
  due: string | null;
  /** Working days past due: 0 = today, negative = days left. */
  overdueDays: number | null;
  command: { key: string; label: string } | null;
  fromMonday: boolean;
  /** reviewed in Ziflow (Monday's "Ziflow" column): the row opens Ziflow (older API versions leave it out) */
  inZiflow?: boolean;
}
export interface MyWorkView {
  today: string;
  yourTurn: MyWorkRow[];
  upNext: MyWorkRow[];
  waiting: { by: { who: string; n: number }[]; rows: MyWorkRow[] };
  /** projects the person hid from their own list (older API versions leave it out) */
  hidden?: HiddenProject[];
}
type TeamLite = Pick<TeamView, 'source' | 'availability' | 'rows'>;

const stockholmToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));

/** "Wednesday 14 October · week 42" / "onsdag 14 oktober · vecka 42" */
export function todayLine(today: string, weekNo?: number, lang: Lang = 'en'): string {
  const t = DICT[lang].home;
  const d = new Date(`${today}T12:00:00Z`);
  return `${t.dow[d.getUTCDay()]} ${d.getUTCDate()} ${t.months[d.getUTCMonth()]}${weekNo ? ` · ${t.week(weekNo)}` : ''}`;
}

/** When a row is due, in words; overdue and today are the only states that get colour (with text, never colour alone). */
export function dueChip(r: Pick<MyWorkRow, 'due' | 'overdueDays'>, lang: Lang = 'en'): { text: string; tone: 'crit' | 'warn' | 'none' } {
  const t = DICT[lang];
  if (!r.due || r.overdueDays === null) return { text: t.home.noDate, tone: 'none' };
  if (r.overdueDays > 0) return { text: t.home.daysLate(r.overdueDays), tone: 'crit' };
  if (r.overdueDays === 0) return { text: t.common.today, tone: 'warn' };
  if (r.overdueDays === -1) return { text: t.common.tomorrow, tone: 'none' };
  return { text: lang === 'sv' ? ddmSv(r.due) : ddm(r.due), tone: 'none' };
}

/** The header line (UX-103): videos counted, and what is late. */
export function summaryLine(v: Pick<MyWorkView, 'yourTurn'>, lang: Lang = 'en'): string {
  const t = DICT[lang].home;
  const n = v.yourTurn.length;
  if (!n) return t.nothingWaits;
  const late = v.yourTurn.filter((r) => (r.overdueDays ?? -1) > 0).length;
  const today = v.yourTurn.filter((r) => r.overdueDays === 0).length;
  const extra = [late ? t.nLate(late) : '', today ? t.nDueToday(today) : ''].filter(Boolean).join(' · ');
  return `${t.needYou(n)}${extra ? ` · ${extra}` : ''}`;
}

/** My Work (Del 1 §1.5.1): your week on top (UX-1003), then your own videos, most urgent first, with absence and news beside them. */
export function Home(p: { api: Api; me: Me }) {
  // editors and camera: one page for employees and freelancers alike, its main column by role (owner 2026-10-21)
  if (isCrewView(p.me.roles) && p.me.modules.includes('projects')) return <CrewToday api={p.api} me={p.me} />;
  return <StaffHome api={p.api} me={p.me} />;
}

function StaffHome(p: { api: Api; me: Me }) {
  const t = useT();
  const lang = useLang();
  const has = (m: string) => p.me.modules.includes(m);
  // a page seen before shows at once and is refreshed underneath (api.ts peek)
  const [team, setTeam] = useState<TeamLite | null>(() => (has('team') ? (peek<TeamLite>(p.api, '/api/workload/team') ?? null) : null));
  const [work, setWork] = useState<MyWorkView | null | 'off'>(() => (has('projects') ? (peek<MyWorkView>(p.api, '/api/projects/my-work') ?? null) : 'off'));
  const loadWork = () => p.api.get<MyWorkView>('/api/projects/my-work').then(setWork, () => setWork('off'));
  useEffect(() => {
    if (has('team')) void p.api.get<TeamLite>('/api/workload/team').then(setTeam, () => setTeam(null));
    if (has('projects')) void loadWork();
  }, []);
  const today = work && work !== 'off' ? work.today : stockholmToday();
  const weekNo = team?.rows[0]?.cells[0]?.weekNo;
  const staffEmployee = p.me.person.kind === 'employee' && p.me.via === 'staff';
  const firstName = p.me.person.name.split(' ')[0] ?? '';
  return (
    <div class="screen mywork">
      <PageHead title={t.nav.myWork} sub={todayLine(today, weekNo, lang)} />
      {has('projects') ? <RushesBanners api={p.api} /> : null}
      {has('projects') ? <PrepBanners api={p.api} /> : null}
      {work === 'off' ? (
        <Callout tone="info" title={t.home.earlyTitle}>
          {t.home.earlyBody}
        </Callout>
      ) : null}
      <div class="mw-cols">
        <div class="mw-main">
          {/* the schedule first, then Din tur, the project tasks, Härnäst and Väntar på andra, each one foldable (owner 2026-11-02) */}
          {p.me.via === 'staff' ? <WeekCalendar api={p.api} admin={p.me.caps.includes('modules.admin')} fold /> : null}
          {p.me.person.kind === 'employee' && has('projects') ? <ReviewsWaiting api={p.api} /> : null}
          {work === null ? <div class="panel mw-skel" aria-busy="true" aria-label={t.home.loadingVideos} /> : null}
          {work && work !== 'off' ? <WorkLists api={p.api} v={work} name={firstName} reload={loadWork} tasks={has('projects') ? <MyTasks api={p.api} /> : null} /> : null}
          {work === 'off' && has('projects') ? <MyTasks api={p.api} /> : null}
          {work === 'off' && !has('team') && !has('news') ? <p class="muted">{t.home.nothingForRole}</p> : null}
          {p.me.roles.some((r) => r === 'editor' || r === 'camera') ? <GettingStarted api={p.api} via="staff" mode="card" post={p.me.modules.includes('post_guide')} /> : null}
        </div>
        <aside class="mw-side" aria-label={t.home.side}>
          {team ? <WeekCard team={team} me={p.me} /> : null}
          {staffEmployee && has('projects') ? <MentionsOnNow api={p.api} /> : null}
          {staffEmployee ? <AbsenceBlock api={p.api} today={today} /> : null}
          {has('news') ? <NewsOnNow api={p.api} open={(id) => navigate(id ? `/news/${id}` : '/news')} /> : null}
        </aside>
      </div>
    </div>
  );
}

/**
 * "Idag" for editors and camera (owner 2026-10-21): the same page for employees and freelancers, so nothing lands on one
 * of them only. Rushes, the week, "Kom igång", then the main column by role: Redigering works from the videos (Din tur
 * with its buttons), Kamera from the shoots. "Nytt sedan i går" beside it. One call for the day (/api/crew/today).
 */
export function CrewToday(p: { api: Api; me: Me }) {
  const lang = useLang();
  const [rev, setRev] = useState(0);
  const day = useToday(p.api, true, true, rev);
  const v = day && day !== 'off' ? day : null;
  const roles = p.me.roles;
  const filming = roles.includes('camera');
  // a freelancer with no working role edits (the old Freelancer role)
  const editing = roles.includes('editor') || !filming;
  const work: MyWorkView | null = v ? { today: v.today, yourTurn: v.yourTurn ?? [], upNext: v.upNext ?? [], waiting: v.waiting ?? { by: [], rows: [] }, hidden: v.hidden ?? undefined } : null;
  const showWork = work && (editing || work.yourTurn.length > 0);
  const reload = async () => setRev((n) => n + 1);
  const staffEmployee = p.me.person.kind === 'employee' && p.me.via === 'staff';
  // both roles (many freelancers edit and film): one page with both columns, the shoots first on a shoot day
  const shootsFirst = filming && (!editing || !!v?.todayEvents.some((e) => e.kind === 'shoot'));
  return (
    <div class="screen mywork mywork-crew">
      <PageHead title={greeting(p.me.person.name, lang)} sub={[todayLine(v?.today ?? stockholmToday(), undefined, lang), v ? todaySummary(v, lang) : ''].filter(Boolean).join(' · ')} />
      <RushesBanners api={p.api} />
      {/* the day before a shoot: "Läs inför inspelningen" (owner 2026-11-01) */}
      <PrepBanners api={p.api} />
      {day === 'off' ? <TodayFailed /> : null}
      {day === null ? <div class="panel mw-skel" aria-busy="true" aria-label={DICT[lang].home.loadingVideos} /> : null}
      {v ? (
        <div class="mw-cols">
          <div class="mw-main">
            {/* a shoot day opens on the shoot (owner 2026-11-01), then the schedule, then the lists, each foldable (owner 2026-11-02) */}
            {shootsFirst ? <TodayShoots v={v} /> : null}
            <WeekCalendar api={p.api} fold />
            {showWork ? <WorkLists api={p.api} v={work} name={p.me.person.name.split(' ')[0] ?? ''} reload={reload} tasks={<MyTasks api={p.api} />} /> : null}
            {filming && !shootsFirst ? <TodayShoots v={v} /> : null}
            <GettingStarted api={p.api} via={p.me.via} mode="card" work={guideWork(roles)} post={p.me.modules.includes('post_guide')} />
          </div>
          <aside class="mw-side" aria-label={DICT[lang].home.side}>
            <TodayNews v={v} />
            {/* absence requests go to Personio from here (employees only) */}
            {staffEmployee ? <AbsenceBlock api={p.api} today={v.today} /> : null}
          </aside>
        </div>
      ) : null}
    </div>
  );
}

/** A long list shows its first rows; "Show all" opens the rest (a producer can have hundreds of videos queued). */
const FIRST_ROWS = 10;
const firstRows = <T,>(rows: T[], all: boolean): T[] => (all ? rows : rows.slice(0, FIRST_ROWS));

function ShowAll(p: { n: number; all: boolean; set: (all: boolean) => void }) {
  const t = DICT[useLang()].home;
  if (p.n <= FIRST_ROWS) return null;
  return (
    <div class="mw-more">
      <button type="button" class="btn btn-ghost" aria-expanded={p.all} onClick={() => p.set(!p.all)}>
        {p.all ? t.showFewer : t.showAll(p.n)}
      </button>
    </div>
  );
}


type Change = { undoId?: string | null; text: string };

function WorkLists(p: { api: Api; v: MyWorkView; name: string; reload: () => Promise<void>; tasks?: ComponentChildren }) {
  const lang = useLang();
  const t = DICT[lang];
  // Din tur open, the rest folded; each heading shows its count, so the headings work as the status tiles (owner 2026-11-02)
  const [turnOpen, toggleTurn] = useFold('turn', true);
  const [nextOpen, toggleNext] = useFold('next', false);
  const [waitOpen, toggleWait] = useFold('wait', false);
  const [busy, setBusy] = useState<string | null>(null);
  const [allTurn, setAllTurn] = useState(false);
  const [allNext, setAllNext] = useState(false);
  const [allWait, setAllWait] = useState(false);
  const [sending, setSending] = useState<MyWorkRow | null>(null);
  const run = async (r: MyWorkRow, pickFile = true) => {
    if (!r.command) return;
    // "Skicka till intern granskning" picks the file too (owner 2026-11-01): file and status in one step
    if (pickFile && (r.command.key === 'submit_internal' || r.command.key === 'submit_final_formats') && !r.inZiflow) return setSending(r);
    setBusy(r.videoId);
    try {
      const out = await p.api.post<Change>(`/api/projects/${r.projectId}/videos/${r.videoId}/command`, { command: r.command.key });
      await p.reload();
      toast({
        text: out.text,
        undo: out.undoId
          ? async () => {
              try {
                await p.api.post(`/api/projects/undo/${out.undoId}`);
                await p.reload();
                toast({ text: t.common.undone });
              } catch (e) {
                toast({ text: e instanceof Error ? e.message : String(e) });
              }
            }
          : undefined
      });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };
  const { v } = p;
  const sendDialog = sending ? <SendForReview api={p.api} projectId={sending.projectId} videoId={sending.videoId} videoName={sending.videoName} fallback={() => run(sending, false)} onDone={p.reload} onClose={() => setSending(null)} /> : null;
  // "Dölj" hides the row's whole project from this person's list (owner 2026-10-20)
  // a Ziflow project's video (owner 2026-10-24): the row's button opens its review in Ziflow
  const zf = (r: MyWorkRow) => void openZiflow(p.api, r.projectId, r.videoId, t.home.openingZiflow);
  const hide = (r: MyWorkRow) => <HideProject api={p.api} projectId={r.projectId} projectName={r.projectName} done={() => void p.reload()} />;
  return (
    <>
      {sendDialog}
      <section class="panel mw-list" aria-labelledby="mw-turn" data-tour="work-turn">
        <FoldHead id="mw-turn" title={t.home.yourTurn} n={v.yourTurn.length} tone={v.yourTurn.some((r) => (r.overdueDays ?? -1) > 0) ? 'crit' : undefined} hint={summaryLine(v, lang)} open={turnOpen} toggle={toggleTurn} />
        {!turnOpen ? null : v.yourTurn.length ? (
          <div id="mw-turn-body">
          <ul class="mw-rows">
            {firstRows(v.yourTurn, allTurn).map((r, i) => (
              <Row r={r} primary={i === 0} busy={busy === r.videoId} run={run} zf={zf} hide={hide} />
            ))}
          </ul>
          <ShowAll n={v.yourTurn.length} all={allTurn} set={setAllTurn} />
          </div>
        ) : (
          <div class="mw-empty" id="mw-turn-body">
            <p>
              <strong>{t.home.allClearName(p.name)}</strong> {t.home.nothingWaitsDot}
            </p>
            <a href="/projects" onClick={go('/projects')}>
              {t.home.seeProjects}
            </a>
          </div>
        )}
      </section>
      {p.tasks}
      {v.upNext.length ? (
        <section class="panel mw-list" aria-labelledby="mw-next">
          <FoldHead id="mw-next" title={t.home.upNext} n={v.upNext.length} hint={t.home.upNextInfo} open={nextOpen} toggle={toggleNext} />
          {nextOpen ? (
            <div id="mw-next-body">
              <ul class="mw-rows">
                {firstRows(v.upNext, allNext).map((r) => (
                  <Row r={r} busy={busy === r.videoId} run={run} zf={zf} hide={hide} />
                ))}
              </ul>
              <ShowAll n={v.upNext.length} all={allNext} set={setAllNext} />
            </div>
          ) : null}
        </section>
      ) : null}
      {v.waiting.rows.length ? (
        <section class="panel mw-list" aria-labelledby="mw-wait">
          <FoldHead id="mw-wait" title={t.home.waiting} n={v.waiting.rows.length} hint={v.waiting.by.map((x) => `${t.home.who[x.who] ?? x.who} ${x.n}`).join(' · ')} open={waitOpen} toggle={toggleWait} />
          {waitOpen ? (
            <div id="mw-wait-body">
              <ul class="mw-rows">
                {firstRows(v.waiting.rows, allWait).map((r) => (
                  <Row r={r} quiet hide={hide} />
                ))}
              </ul>
              <ShowAll n={v.waiting.rows.length} all={allWait} set={setAllWait} />
            </div>
          ) : null}
        </section>
      ) : null}
      <HiddenProjects api={p.api} hidden={v.hidden} done={() => void p.reload()} />
    </>
  );
}

/** One video: when, what, where it is, and at most one button (the first row's is the page's only yellow one, UX-012). */
function Row(p: { r: MyWorkRow; primary?: boolean; busy?: boolean; quiet?: boolean; run?: (r: MyWorkRow) => void; zf?: (r: MyWorkRow) => void; hide?: (r: MyWorkRow) => ComponentChildren }) {
  const { r } = p;
  const lang = useLang();
  const t = DICT[lang];
  const due = dueChip(r, lang);
  const href = `/projects/${r.projectId}`;
  // the video's name opens its review (owner 2026-11-01); a Ziflow project's video opens the project
  const nameHref = r.inZiflow ? href : `/projects/${r.projectId}/review/${r.videoId}`;
  const phrase = p.quiet ? r.next.phrase : r.next.phrase.replace(/^(Your turn|Din tur): /, '');
  // the button already says it ("Submit for internal review"), so the step line only adds what the button doesn't
  const sameAsButton = !p.quiet && r.command && phrase.toLowerCase() === r.command.label.toLowerCase();
  return (
    <li class={`mw-row${p.primary ? ' is-first' : ''}`}>
      <span class={`mw-due due-${due.tone}`}>
        {due.tone === 'crit' ? (
          <span class="mw-due-ico" aria-hidden="true">
            !
          </span>
        ) : null}
        {due.text}
      </span>
      <span class="mw-what">
        <a class="mw-name" href={nameHref} onClick={go(nameHref)} title={r.videoName}>
          {r.videoName}
        </a>
        <span class="mw-where" title={`${r.client} · ${r.projectName}`}>
          {r.client} · {r.projectName}
        </span>
      </span>
      <span class="mw-step">
        {sameAsButton ? null : (
          <span class="mw-phrase">
            {p.quiet && r.next.who?.length ? <AvatarStack people={r.next.who} max={3} size="sm" label={t.home.waitingOn} /> : null}
            {phrase}
          </span>
        )}
        <StageBar stage={r.stage} compact />
      </span>
      <span class="mw-act">
        {p.hide?.(r)}
        {p.quiet ? null : (
          <button type="button" class="btn btn-sm btn-ghost mw-timer" aria-label={t.home.startTimerOn(r.projectName)} title={t.home.startTimerTitle} onClick={() => openTimer({ mode: 'start', projectId: r.projectId })}>
            <Icon name="play" />
          </button>
        )}
        {p.quiet ? null : r.command ? (
          <button type="button" class={`btn btn-sm${p.primary ? ' btn-primary' : ''}`} disabled={p.busy} onClick={() => p.run?.(r)}>
            {p.busy ? t.common.saving : r.command.label}
          </button>
        ) : r.inZiflow && p.zf ? (
          <button type="button" class={`btn btn-sm${p.primary ? ' btn-primary' : ''}`} title={t.home.inZiflowTitle} onClick={() => p.zf?.(r)}>
            {t.home.openInZiflow} <Icon name="external-link" />
          </button>
        ) : (
          <a class={`btn btn-sm${p.primary ? ' btn-primary' : ''}`} href={href} onClick={go(href)}>
            {r.fromMonday ? t.common.open : t.home.openProject}
          </a>
        )}
      </span>
    </li>
  );
}

/** Your own week when you are on the Team list; otherwise (producers, admin) the team's over-capacity line. */
function WeekCard(p: { team: TeamLite; me: Me }) {
  const lang = useLang();
  const t = DICT[lang];
  const own = p.team.rows.find((r) => r.personId === p.me.person.id);
  const canTeam = p.me.caps.includes('team.view');
  return (
    <section class="blk mw-week" aria-labelledby="mw-week-h" data-tour="team-week">
      <div class="blk-head">
        <h2 class="blk-title" id="mw-week-h">
          {own ? t.home.myWeeks : t.nav.team}
        </h2>
        <a href={own ? `/team/${encodeURIComponent(own.personKey)}` : '/team'} onClick={go(own ? `/team/${encodeURIComponent(own.personKey)}` : '/team')}>
          {own ? t.home.seeMyWeeks : t.home.openTeam}
        </a>
      </div>
      {own ? (
        <ul class="mw-weeks">
          {own.cells.slice(0, 3).map((c, i) => {
            const tone = look(c);
            return (
              <li class={`tone-${tone}`}>
                <span class="mw-wk">{i === 0 ? t.home.thisWeek(c.weekNo) : t.home.weekShort(c.weekNo)}</span>
                <span class="wk-bar" aria-hidden="true">
                  <i style={`width:${tone === 'miss' || tone === 'empty' ? 0 : barPct(c)}%`} />
                </span>
                <span class="mw-pct">{tone === 'empty' ? '–' : tone === 'miss' ? t.home.noAvailability(h(c.demandH)) : t.home.ofCapacity(pctText(c, ' ', lang), h(c.demandH), h(c.capacityH))}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {canTeam ? <p class="small">{overText(p.team.rows, lang)}</p> : null}
      {p.team.availability ? (
        <p class="small">
          {p.team.availability.text} ·{' '}
          <a href={`/team/availability?month=${p.team.availability.month}`} onClick={go(`/team/availability?month=${p.team.availability.month}`)}>
            {t.common.open}
          </a>
        </p>
      ) : null}
      <p class="small muted">{p.team.source.label}</p>
    </section>
  );
}

/** The Team card's one line: who is over capacity in the next two weeks (from the same view as Team). */
export function overText(rows: TeamView['rows'], lang: Lang = 'en'): string {
  const t = DICT[lang].home;
  const over = rows.filter((r) => r.cells.slice(0, 2).some((c) => c.tone === 'crit'));
  const weeks = rows[0]?.cells.slice(0, 2).map((c) => c.weekNo) ?? [];
  const span = weeks.length === 2 ? t.weeksSpan(weeks[0]!, weeks[1]!) : t.nextTwoWeeks;
  if (!over.length) return t.nobodyOver(span);
  const names = over.map((r) => r.name.split(' ')[0]);
  return `${t.nOver(over.length, span)}: ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3}` : ''}`;
}
